// Libs
import classNames from 'classnames/bind';
import { useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, CardHeader, CardMedia, Tooltip, Typography, Zoom } from '@mui/material';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseMoreAction } from '~/components/common';
// Others
import { checkTypeImage, formatDate, getFullName } from '~/utils/helper/common';
import { IDocument } from '~/utils/interface/document';
import { TDocumentType } from '~/utils/type/common';
// Styles, images, icons
import styles from './DocumentItem.module.scss';
import { icons } from '~/assets';

type Props = {
  isAction?: boolean;
  isSelected?: boolean;
  document: IDocument;
  onclick?: (file: IDocument) => void;
  onEdit?: (file: IDocument) => void;
  onDelete?: (file: IDocument) => void;
  type?: TDocumentType;
};

const cx = classNames.bind(styles);

const DocumentItem = (props: Props) => {
  //#region Destructuring Props
  const { document, isSelected, type, isAction = false, onEdit, onDelete, onclick } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const nameRef = useRef<HTMLParagraphElement | null>(null);
  const { t } = useTranslation(['admin/document', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isTruncate, setIsTruncate] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (nameRef.current) {
      setIsTruncate(nameRef.current.scrollWidth > nameRef.current.clientWidth);
    }
  }, [document?.name]);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Card
      component={'div'}
      className={cx('container', isAction && 'action', isSelected && 'selected')}
      onClick={() => document && onclick && onclick(document)}
    >
      {!isAction && (
        <CardHeader
          className={cx('header')}
          action={
            <BaseMoreAction
              icon={icons.commonIconMoreInactive}
              {...(type === 'local'
                ? {
                    actions: [
                      {
                        hasOtherColor: true,
                        label: t('table_action_delete', { ns: 'common/base' }),
                        icon: (
                          <img
                            src={icons.tableIconActionDelete}
                            alt={t('common_alt_img', { ns: 'common/base' })}
                            width={16}
                            height={16}
                          />
                        ),
                        onClick: () => document && onDelete && onDelete(document),
                      },
                    ],
                  }
                : {
                    actions: [
                      {
                        label: t('table_action_edit', { ns: 'common/base' }),
                        icon: (
                          <img
                            src={icons.tableIconActionEdit}
                            alt={t('common_alt_img', { ns: 'common/base' })}
                            width={16}
                            height={16}
                          />
                        ),
                        onClick: () => document && onEdit && onEdit(document),
                      },
                      {
                        hasOtherColor: true,
                        label: t('table_action_delete', { ns: 'common/base' }),
                        icon: (
                          <img
                            src={icons.tableIconActionDelete}
                            alt={t('common_alt_img', { ns: 'common/base' })}
                            width={16}
                            height={16}
                          />
                        ),
                        onClick: () => document && onDelete && onDelete(document),
                      },
                    ],
                  })}
            />
          }
        />
      )}

      {checkTypeImage(document.mimetype) ? (
        <CardMedia
          component={'img'}
          alt={t('common_alt_img', { ns: 'common/base' })}
          className={cx('image')}
          image={document.url}
          onClick={() => window.open(document?.url)}
        />
      ) : (
        <CardMedia
          component={'img'}
          alt={t('common_alt_img', { ns: 'common/base' })}
          className={cx('iconDocument')}
          image={icons.documentIconDocument}
          onClick={() => window.open(document?.url)}
        />
      )}

      <CardContent className={cx('content')}>
        <Box className={cx('name')}>
          {isTruncate ? (
            <Tooltip
              placement='top'
              title={document?.name}
              arrow
              slots={{
                transition: Zoom,
              }}
            >
              <Typography ref={nameRef} component='p' className={cx('textName')}>
                {document?.name}
              </Typography>
            </Tooltip>
          ) : (
            <Typography ref={nameRef} component='p' className={cx('textName')}>
              {document?.name}
            </Typography>
          )}
        </Box>

        {type !== 'local' && (
          <Box className={cx('infoCreated')}>
            <Box>
              <Typography component='p' className={cx('label')}>
                {t('tab_documents_label_create_by')}
              </Typography>

              <Typography component='p' className={cx('value')}>
                {getFullName({ ...document.createdBy })}
              </Typography>
            </Box>

            <Box>
              <Typography component='p' className={cx('label')}>
                {t('tab_documents_label_create_at')}
              </Typography>

              <Typography component='p' className={cx('value')}>
                {formatDate(document?.createdAt)}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentItem;
